import { differenceInMilliseconds } from 'date-fns';
import { GeoJSON, LatLng, LatLngTuple } from 'leaflet';
import { decode } from '@here/flexpolyline';
import { uniqBy } from 'lodash-es';
import {
  along as turfAlong,
  bezierSpline,
  distance as turfDistance,
  length as turfLength,
  lineString as turfLineString,
  point as turfPoint,
} from '@turf/turf';
import {
  AircraftMarkerListItem,
  AircraftMarkerType,
  AircraftStatusEnum,
  ContractorSurgeonMarkerType,
  HospitalMarkerType,
  HubMarkerType,
  MapFilterType,
  MarkerTypeEnum,
  RouteMarkerType,
  RoutePolylineType,
  SurgeonMarkerType,
  TxCenterMarkerType,
} from '@lp/features/RoutePlanner/types';
import { parseZonedDateTime } from '@internationalized/date';
import { Hub } from '@/api/apis/facilities';
import {
  AircraftAvailabilityView,
  CaseMatchType,
  CrewDutyAvailabilityView,
  CSAssignmentCandidate,
  OcsConsoleData,
  RSAssignmentCandidate,
  RSContractorAssignmentCandidate,
} from '@/api/apis/resources';
import {
  AircraftLegResource,
  AviaLegTypes,
  BaseLegResourceType,
  GroundLegTypes,
  HubRouteLocation,
  LegLocationTypeEnum,
  RouteLegTypeEnum,
  RouteLocationType,
  SegmentTypeEnum,
} from '@/api/apis/route';
import { CalculateGroundTimeType } from '@/api/apis/here-maps';
import { CaseFacility, CaseResourceType, OrganType } from '@/api/apis/case';
import { queryClient } from '@/queries';
import { hereMapsQueries } from '@/queries/here-maps/here-maps.query';
import { getTzDateTimeString } from '@/utils/helpers';

export const TRANSPORT_PROVIDER_ABBR: Record<RouteLegTypeEnum, string> = {
  [RouteLegTypeEnum.TmdxJetAviaLeg]: '',
  [RouteLegTypeEnum.CurriServiceGroundLeg]: 'COM',
  [RouteLegTypeEnum.TxCenterProviderGroundLeg]: 'TX',
  [RouteLegTypeEnum.TxCenterProviderAviaLeg]: 'TX',
  [RouteLegTypeEnum.SupplementalLiftAviaLeg]: 'SLP',
  [RouteLegTypeEnum.TmdxAffiliateGroundLeg]: 'A',
  [RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg]: 'A',
  [RouteLegTypeEnum.TmdxDriveTedsGroundLeg]: 'TEDS',
  [RouteLegTypeEnum.CommercialFlightAviaLeg]: 'COM',
  [RouteLegTypeEnum.AccommodationLeg]: '',
  [RouteLegTypeEnum.SelfServicedGroundLeg]: 'S',
};

type GetAircraftItemProps = {
  aircraft: AircraftAvailabilityView;
  filters?: Pick<Record<MapFilterType, boolean | string>, MapFilterType.from | MapFilterType.to> | null;
  shouldDateIncludes?: boolean;
  caseId?: string;
};

const getDutyStatus = ({
  filters,
  availabilities,
}: Pick<GetAircraftItemProps, 'filters'> & { availabilities: CrewDutyAvailabilityView[] }) => {
  return filters &&
    availabilities.every((a) => differenceInMilliseconds(new Date(a.startUtc), new Date(filters.from as string)) > 0)
    ? AircraftStatusEnum.onRest
    : AircraftStatusEnum.onDuty;
};

export const getAircraftItem = ({
  aircraft,
  filters,
  shouldDateIncludes = false,
}: GetAircraftItemProps): AircraftMarkerListItem => {
  const currentAvailabilities = filters
    ? aircraft.predictedAvailabilities.filter((a) => {
        const isStartBefore = differenceInMilliseconds(new Date(filters.to as string), new Date(a.startUtc)) >= 0;
        const isEndAfterStart = differenceInMilliseconds(new Date(a.endUtc), new Date(filters.from as string)) >= 0;
        const isAvailable = shouldDateIncludes ? isEndAfterStart : isStartBefore && isEndAfterStart;

        return isAvailable && !!a.airport;
      })
    : aircraft.predictedAvailabilities.filter((a) => !!a.airport);

  const dutyStatus = getDutyStatus({ filters, availabilities: currentAvailabilities });

  return {
    id: aircraft.id,
    availabilities: currentAvailabilities,
    reservations: aircraft.reservations,
    registration: aircraft.registration,
    latitude: aircraft.latitude,
    longitude: aircraft.longitude,
    status: dutyStatus,
  };
};

export const getAircraftMarkers = (
  filters: Record<MapFilterType, boolean | string>,
  list: AircraftAvailabilityView[] = [],
): AircraftMarkerType[] => {
  const aircraftByAirport = new Map<string, AircraftMarkerType>();

  list.forEach((aircraft) => {
    const aircraftItem = getAircraftItem({ aircraft, filters });

    if (!aircraftItem.availabilities.length) {
      return;
    }

    if (!filters.showReserved && aircraftItem.status === AircraftStatusEnum.reserved) {
      return;
    }

    const currentAirport = aircraftItem.availabilities[0].airport;
    const airportKey = currentAirport?.iata || currentAirport?.icao;

    if (!airportKey) {
      return;
    }

    const existingMarker = aircraftByAirport.get(airportKey);

    if (!existingMarker) {
      aircraftByAirport.set(airportKey, {
        type: MarkerTypeEnum.aircraft,
        id: airportKey,
        name: `${airportKey} (${currentAirport.servedCity})`,
        timezone: currentAirport.ianaTimeZone,
        location: new LatLng(currentAirport.latitude, currentAirport.longitude),
        list: [aircraftItem],
      } as AircraftMarkerType);
      return;
    }

    existingMarker.list.push(aircraftItem);
  });

  aircraftByAirport.forEach((marker) => {
    marker.list.sort((a) => (a.reservations.length ? 1 : -1));
  });

  return Array.from(aircraftByAirport.values());
};

export const getDonorHospitalMarker = (hospital: CaseFacility): HospitalMarkerType => ({
  type: MarkerTypeEnum.donorHospital,
  id: hospital.id,
  code: hospital.code,
  location: new LatLng(hospital.location.latitude, hospital.location.longitude),
});

export const getTxCenterMarker = (txCenter: CaseFacility): TxCenterMarkerType => ({
  type: MarkerTypeEnum.txCenter,
  id: txCenter.id,
  code: txCenter.code,
  location: new LatLng(txCenter.location.latitude, txCenter.location.longitude),
});

export const getSurgeonMarkers = (
  filters: Record<MapFilterType, boolean | string>,
  sCandidates: RSAssignmentCandidate[] = [],
  contractorCandidates: RSContractorAssignmentCandidate[] = [],
): (SurgeonMarkerType | ContractorSurgeonMarkerType)[] => {
  if (!filters.surgeon) return [];

  const sMarkers = sCandidates
    .filter((i) => !!filters.showReserved || i.match.caseType === CaseMatchType.AssignmentCandidate)
    .map<SurgeonMarkerType>((i) => ({
      type: MarkerTypeEnum.surgeon,
      id: i.employeeId,
      location: new LatLng(i.homeLocation?.latitude || 0, i.homeLocation?.longitude || 0),
      isContractor: false,
      item: i,
    }));

  const contractorMarkers = contractorCandidates
    .filter((i) => !!filters.showReserved || i.match.caseType === CaseMatchType.AssignmentCandidate)
    .map<ContractorSurgeonMarkerType>((i) => ({
      type: MarkerTypeEnum.surgeon,
      id: i.employeeId,
      location: new LatLng(i.homeLocation?.latitude || 0, i.homeLocation?.longitude || 0),
      isContractor: true,
      item: i,
    }));

  return [...sMarkers, ...contractorMarkers];
};

type GetHubMarkersParams = {
  organType: OrganType;
  filters: Record<MapFilterType, boolean | string>;
  hubs: Hub[];
  csList?: CSAssignmentCandidate[];
  devicesList?: OcsConsoleData[];
};

const groupCsByHubId = (
  csList: CSAssignmentCandidate[] | undefined,
  showReserved: boolean,
): Map<string, CSAssignmentCandidate[]> => {
  const csByHubId = new Map<string, CSAssignmentCandidate[]>();

  csList?.forEach((i) => {
    const hubId = i.shiftHubId || i.baseLocation;
    const isEligible = showReserved || i.match.caseType === CaseMatchType.AssignmentCandidate;

    if (!hubId || !isEligible) return;

    const list = csByHubId.get(hubId) || [];
    list.push(i);
    csByHubId.set(hubId, list);
  });

  return csByHubId;
};

const groupDevicesByHubId = (
  devicesList: OcsConsoleData[] | undefined,
  organType: OrganType,
): Map<string, OcsConsoleData[]> => {
  const devicesByHubId = new Map<string, OcsConsoleData[]>();

  devicesList?.forEach((i) => {
    if (i.organType !== organType || i.isInMaintenance) return;

    const list = devicesByHubId.get(i.nopHub) || [];
    list.push(i);
    devicesByHubId.set(i.nopHub, list);
  });

  return devicesByHubId;
};

export const getHubMarkers = ({
  organType,
  filters,
  hubs,
  csList,
  devicesList,
}: GetHubMarkersParams): HubMarkerType[] => {
  const csByHubId = groupCsByHubId(csList, !!filters.showReserved);
  const devicesByHubId = groupDevicesByHubId(devicesList, organType);

  return hubs.map<HubMarkerType>((h) => {
    const cs = csByHubId.get(h.id) || [];
    const devices = devicesList ? devicesByHubId.get(h.id) || [] : undefined;

    return {
      type: MarkerTypeEnum.hub,
      id: h.id,
      code: h.code,
      name: h.name,
      location: new LatLng(h.location.latitude, h.location.longitude),
      ...(filters.ocsDevices ? { devices } : {}),
      ...(filters.clinicalSpecialists
        ? {
            clinicalSpecialists: cs,
          }
        : {}),
    };
  });
};

type ClosestHubType = {
  distance: number;
  hub: HubMarkerType | null;
};

export const getClosestHubByDistance = (hubs: HubMarkerType[], hospital: HospitalMarkerType) => {
  return hubs.reduce(
    (acc, h) => {
      const hubLocation = h.location as LatLng;
      const donorHospitalLocation = hospital.location as LatLng;

      const distance = turfDistance(
        turfPoint([hubLocation.lng, hubLocation.lat]),
        turfPoint([donorHospitalLocation.lng, donorHospitalLocation.lat]),
        { units: 'kilometers' },
      );

      if (distance < acc.distance) {
        return {
          hub: h,
          distance,
        };
      }

      return acc;
    },
    { distance: Infinity, hub: null } as ClosestHubType,
  );
};

export const getAirMiddlePoint = (coordsStart: LatLng, coordsEnd: LatLng): LatLng => {
  const offsetX = coordsEnd.lng - coordsStart.lng;
  const offsetY = coordsEnd.lat - coordsStart.lat;

  const r = Math.sqrt(offsetX ** 2 + offsetY ** 2);
  const theta = Math.atan2(offsetY, offsetX);

  const thetaOffset = 3.14 / 50;

  const r2 = r / 2 / Math.cos(thetaOffset);
  const theta2 = theta + thetaOffset;

  const midpointX = r2 * Math.cos(theta2) + coordsStart.lng;
  const midpointY = r2 * Math.sin(theta2) + coordsStart.lat;

  return new LatLng(midpointY, midpointX);
};

export const getCurvedPolyline = (startLocation: LatLng, endLocation: LatLng): LatLng[] => {
  const start = turfPoint([startLocation.lng, startLocation.lat]);
  const end = turfPoint([endLocation.lng, endLocation.lat]);

  const midpoint = getAirMiddlePoint(endLocation, startLocation);

  return GeoJSON.coordsToLatLngs(
    bezierSpline({
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: [start.geometry.coordinates, [midpoint.lng, midpoint.lat], end.geometry.coordinates],
      },
    }).geometry.coordinates,
  ) as LatLng[];
};

export const getGroundMiddlePoint = (coordinates: LatLngTuple[]): LatLng => {
  const coordsToGeo = coordinates.map((i) => turfPoint([i[1], i[0]]).geometry.coordinates);
  const line = turfLineString(coordsToGeo);
  const totalDistance = turfLength(line, { units: 'kilometers' });
  const center = turfAlong(line, totalDistance / 2, { units: 'kilometers' });

  return new LatLng(center.geometry.coordinates[1], center.geometry.coordinates[0]);
};

export const getIconMapColor = (type: SegmentTypeEnum): string => {
  switch (type) {
    case SegmentTypeEnum.hospital:
      return '#E78521';
    case SegmentTypeEnum.txcenter:
      return '#9B1C1C';
    default:
      return '#0F8B99';
  }
};

const buildLegPolyline = async (
  leg: AviaLegTypes | GroundLegTypes,
  locations: RouteLocationType[],
  aircrafts: BaseLegResourceType<AircraftLegResource>[],
  isMultipleAircrafts: boolean,
): Promise<RoutePolylineType | null> => {
  if (!leg.departureLocation.locationId || !leg.arrivalLocation.locationId) {
    return null;
  }

  const startLocation = locations.find((l) => l.id === leg.departureLocation.locationId);
  const endLocation = locations.find((l) => l.id === leg.arrivalLocation.locationId);

  if (!startLocation || !endLocation) {
    return null;
  }

  const start = new LatLng(startLocation.location.latitude, startLocation.location.longitude);
  const end = new LatLng(endLocation.location.latitude, endLocation.location.longitude);
  const direction = start.lng > end.lng ? 'left' : 'right';
  const currentAircraft = leg.resources.find((l) => l.resourceType === CaseResourceType.Aircraft);
  const currentAircraftIndex = isMultipleAircrafts
    ? aircrafts.findIndex((a) => a.id === currentAircraft?.resourceId)
    : -1;
  const index = currentAircraftIndex + 1 > 0 ? `${currentAircraftIndex + 1}` : '';
  const typeIndex = TRANSPORT_PROVIDER_ABBR[leg.legType];

  switch (leg.legType) {
    case RouteLegTypeEnum.TmdxJetAviaLeg:
    case RouteLegTypeEnum.SupplementalLiftAviaLeg:
    case RouteLegTypeEnum.TxCenterProviderAviaLeg:
    case RouteLegTypeEnum.CommercialFlightAviaLeg:
      return {
        id: `route-${leg.id}`,
        type: leg.segmentType,
        legType: leg.legType,
        positions: getCurvedPolyline(start, end),
        middlePosition: getAirMiddlePoint(end, start),
        direction,
        index: typeIndex || index,
      };
    case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
    case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
    case RouteLegTypeEnum.TxCenterProviderGroundLeg:
    case RouteLegTypeEnum.CurriServiceGroundLeg:
    case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
    case RouteLegTypeEnum.SelfServicedGroundLeg: {
      const departureTimeUtc = parseZonedDateTime(getTzDateTimeString(leg.departureTime)).toAbsoluteString();

      const { polylines } = await queryClient.ensureQueryData(
        hereMapsQueries.getGroundRoute({
          startLocation: start,
          endLocation: end,
          type: CalculateGroundTimeType.DepartureTime,
          timestamp: departureTimeUtc,
        }),
      );

      const decoded = polylines
        .map((p) => {
          const data = decode(p);

          return data.polyline as LatLngTuple[];
        })
        .flat();

      if (!decoded.length) {
        return null;
      }

      return {
        id: `route-${leg.id}`,
        type: leg.segmentType,
        legType: leg.legType,
        positions: decoded,
        middlePosition: getGroundMiddlePoint(decoded),
        index: typeIndex,
        direction,
      };
    }
    default:
      return null;
  }
};

export const generatePathRouting = async (
  legs: (AviaLegTypes | GroundLegTypes)[],
  locations: RouteLocationType[],
  aircrafts: BaseLegResourceType<AircraftLegResource>[] = [],
): Promise<RoutePolylineType[]> => {
  if (!legs.length) {
    return [];
  }

  const isMultipleAircrafts = aircrafts.length > 1;

  const polylines = await Promise.all(
    legs.map((leg) => buildLegPolyline(leg, locations, aircrafts, isMultipleAircrafts)),
  );

  return polylines.filter((polyline): polyline is RoutePolylineType => polyline !== null);
};

export const generateMarkers = <T extends RouteMarkerType>(
  legs: (AviaLegTypes | GroundLegTypes)[],
  locationType: LegLocationTypeEnum,
  mapper: (locationId: string) => T,
): T[] => {
  const locations = legs
    .filter((l) => l.departureLocation.locationType === locationType || l.arrivalLocation.locationType === locationType)
    .map((l) => {
      const isStartLocation = l.departureLocation.locationType === locationType;
      const isEndLocation = l.arrivalLocation.locationType === locationType;

      return [
        ...(isStartLocation ? [mapper(l.departureLocation.locationId)] : []),
        ...(isEndLocation ? [mapper(l.arrivalLocation.locationId)] : []),
      ];
    })
    .flat();

  return uniqBy<T>(locations, 'id');
};

export const getHubMarker = (data?: HubRouteLocation): HubMarkerType | null => {
  if (!data) return null;

  return {
    type: MarkerTypeEnum.hub,
    id: data.id,
    code: data.code,
    name: data.name,
    location: new LatLng(data.location.latitude, data.location.longitude),
  };
};
